import { CartUpdateEvent, ThemeEvents } from '@theme/events';

const GIFT_VARIANT_ID = 47179161370812;
const GIFT_PRODUCT_ID = '9475365667004';
const GIFT_PRODUCT_HANDLE = 'special-gift-box-and-gift-card';
const SOURCE_ID = 'bluvvy-free-gift';
const PRIVATE_PROPERTY = '_bluvvy_free_gift';
const SYNC_DELAY = 120;

let syncTimer;
let syncing = false;
let currentGiftKeys = new Set();

function getCartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((component) => component instanceof HTMLElement ? component.dataset.sectionId : null)
    .filter(Boolean);
}

async function requestCart() {
  const response = await fetch('/cart.js', {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Cart request failed: ${response.status}`);
  }

  return response.json();
}

async function mutateCart(path, payload) {
  const sectionIds = getCartSectionIds();
  const response = await fetch(path, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ...payload,
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    }),
  });

  const data = await response.json();

  if (!response.ok || data.status) {
    throw new Error(data.description || data.message || `Cart update failed: ${response.status}`);
  }

  return data;
}

function isGiftItem(item) {
  return Number(item.variant_id) === GIFT_VARIANT_ID || item.properties?.[PRIVATE_PROPERTY] === 'true';
}

function decorateGiftRows(cart) {
  currentGiftKeys = new Set(cart.items.filter(isGiftItem).map((item) => item.key));

  document.querySelectorAll('.cart-items__table-row[data-key]').forEach((row) => {
    if (!(row instanceof HTMLElement) || !currentGiftKeys.has(row.dataset.key)) return;

    row.classList.add('bluvvy-free-gift-line');

    const quantityCell = row.querySelector('.cart-items__quantity');
    if (quantityCell) {
      quantityCell.innerHTML = '<span class="bluvvy-free-gift-line__included">Complimentary</span>';
    }

    const priceCell = row.querySelector('.cart-items__price');
    if (priceCell) {
      priceCell.innerHTML = [
        '<span class="bluvvy-free-gift-line__price" style="display:inline-flex;align-items:baseline;gap:0.5rem;white-space:nowrap;">',
        '<s aria-label="Regular price 15 US dollars">$15.00</s>',
        '<strong aria-label="Gift price 0 US dollars">$0.00</strong>',
        '</span>',
      ].join('');
    }

    const unitPrice = row.querySelector('.cart-items__unit-price-wrapper');
    if (unitPrice instanceof HTMLElement) {
      unitPrice.hidden = true;
    }
  });

  hideGiftProductCards();
}

function hideGiftProductCards() {
  const selectors = [
    `product-card[data-product-id="${GIFT_PRODUCT_ID}"]`,
    `a[href*="/products/${GIFT_PRODUCT_HANDLE}"]`,
  ];

  document.querySelectorAll(selectors.join(',')).forEach((element) => {
    if (element.closest('.cart-items__table-row')) return;

    const card = element.closest('.resource-list__item, product-card, resource-card');
    if (card instanceof HTMLElement && card.isConnected) {
      card.remove();
    }
  });
}

function renderAfterCartUpdate(cart) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => decorateGiftRows(cart));
  });
}

async function normalizeGift() {
  if (syncing) return;
  syncing = true;

  try {
    let cart = await requestCart();
    let giftItems = cart.items.filter(isGiftItem);
    const hasPaidItem = cart.items.some((item) => !isGiftItem(item));
    let lastMutation;

    if (!hasPaidItem && giftItems.length) {
      for (const giftItem of giftItems) {
        lastMutation = await mutateCart('/cart/change.js', {
          id: giftItem.key,
          quantity: 0,
        });
      }
    } else if (hasPaidItem && giftItems.length === 0) {
      lastMutation = await mutateCart('/cart/add.js', {
        items: [
          {
            id: GIFT_VARIANT_ID,
            quantity: 1,
            properties: {
              [PRIVATE_PROPERTY]: 'true',
            },
          },
        ],
      });
    } else if (hasPaidItem && giftItems.length) {
      const [giftToKeep, ...duplicateGifts] = giftItems;

      if (giftToKeep.quantity !== 1) {
        lastMutation = await mutateCart('/cart/change.js', {
          id: giftToKeep.key,
          quantity: 1,
        });
      }

      for (const duplicateGift of duplicateGifts) {
        lastMutation = await mutateCart('/cart/change.js', {
          id: duplicateGift.key,
          quantity: 0,
        });
      }
    }

    cart = await requestCart();
    decorateGiftRows(cart);

    if (lastMutation?.sections) {
      document.dispatchEvent(
        new CartUpdateEvent(cart, SOURCE_ID, {
          source: SOURCE_ID,
          itemCount: cart.item_count,
          sections: lastMutation.sections,
        })
      );
      renderAfterCartUpdate(cart);
    }
  } catch (error) {
    console.error('[Bluvvy free gift]', error);
  } finally {
    syncing = false;
  }
}

function scheduleGiftSync(delay = SYNC_DELAY) {
  window.clearTimeout(syncTimer);
  syncTimer = window.setTimeout(normalizeGift, delay);
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event.detail?.data?.source === SOURCE_ID) return;
  scheduleGiftSync();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => scheduleGiftSync(0), { once: true });
} else {
  scheduleGiftSync(0);
}

const cartObserver = new MutationObserver(() => {
  hideGiftProductCards();

  if (!currentGiftKeys.size) return;

  document.querySelectorAll('.cart-items__table-row[data-key]').forEach((row) => {
    if (row instanceof HTMLElement && currentGiftKeys.has(row.dataset.key) && !row.classList.contains('bluvvy-free-gift-line')) {
      scheduleGiftSync(0);
    }
  });
});

cartObserver.observe(document.documentElement, {
  childList: true,
  subtree: true,
});
